import { useId } from 'react'
import type { FrameParams, FrameVariant } from '@/domain/dialGuide'

/**
 * A simulated photograph rendered as SVG: an evening street with hills, a sun,
 * a lamppost, a foreground figure, and a cyclist. The four FrameParams knobs
 * reproduce what the real settings do — motion streaks the cyclist, dof melts
 * the background, grain speckles the frame, exposure shifts brightness.
 *
 * Simulated rather than photographed on purpose: every frame in a row is the
 * identical scene, so the one thing that changed is the one thing you see.
 */
export function SampleFrame({ params }: { params: FrameParams }) {
  const uid = useId()
  const blurId = `dg-blur-${uid}`
  const grainId = `dg-grain-${uid}`

  const { motion, dof, grain, exposure } = params

  // Ghost copies of the cyclist trail backwards as the shutter slows.
  const ghosts = Math.round(motion * 5)

  return (
    <svg viewBox="0 0 160 110" className="w-full rounded-lg" aria-hidden="true">
      <defs>
        <linearGradient id={`sky-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2b3a55" />
          <stop offset="70%" stopColor="#8a6d55" />
          <stop offset="100%" stopColor="#b0876a" />
        </linearGradient>
        <filter id={blurId}>
          <feGaussianBlur stdDeviation={dof * 2.6} />
        </filter>
        <filter id={grainId}>
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.5  0 0 0 0 0.5  0 0 0 0 0.5  0 0 0 0.9 0" />
          <feComposite operator="in" in2="SourceGraphic" />
        </filter>
      </defs>

      {/* Background: sky, sun, hills, lamppost — blurred by aperture */}
      <g filter={dof > 0.05 ? `url(#${blurId})` : undefined}>
        <rect width="160" height="110" fill={`url(#sky-${uid})`} />
        <circle cx="118" cy="34" r={9 + dof * 3} fill="#e8d9a8" opacity={0.95} />
        <path d="M0 68 Q 30 46 62 66 T 160 62 V 110 H 0 Z" fill="#3d4152" />
        <path d="M0 80 Q 44 62 90 78 T 160 74 V 110 H 0 Z" fill="#2c2f3c" />
        {/* Lamppost on the far side of the street */}
        <rect x="98" y="46" width="2.5" height="34" fill="#191b22" />
        <circle cx="99.2" cy="44" r="3.4" fill="#e6c987" opacity="0.9" />
      </g>

      {/* Road — mid-ground, lightly blurred at most */}
      <path d="M0 96 L160 88 V 110 H 0 Z" fill="#22232b" />

      {/* Cyclist: ghosts trail behind when the shutter is slow */}
      <g>
        {motion > 0.1 && (
          <rect
            x={62 - motion * 34}
            y="76"
            width={motion * 40}
            height="10"
            rx="5"
            fill="#c9c6bf"
            opacity={0.18 + motion * 0.12}
          />
        )}
        {Array.from({ length: ghosts }, (_, i) => {
          const t = (i + 1) / (ghosts + 1)
          return (
            <Cyclist
              key={i}
              x={64 - t * motion * 30}
              opacity={0.28 * (1 - t)}
              squash={motion * 0.4}
            />
          )
        })}
        <Cyclist x={64} opacity={1} squash={motion * 0.55} />
      </g>

      {/* Foreground figure — always sharp; depth of field never touches it */}
      <g fill="#101116">
        <circle cx="26" cy="70" r="6" />
        <path d="M18 110 V 84 q 0 -8 8 -8 q 8 0 8 8 V 110 Z" />
      </g>

      {/* Grain, then exposure shift on top of everything */}
      {grain > 0.01 && (
        <rect width="160" height="110" filter={`url(#${grainId})`} opacity={grain * 0.55} />
      )}
      {exposure !== 0 && (
        <rect
          width="160"
          height="110"
          fill={exposure > 0 ? '#ffffff' : '#000000'}
          opacity={Math.abs(exposure) * 0.55}
        />
      )}
      <rect width="160" height="110" fill="none" stroke="rgba(255,255,255,.08)" rx="0" />
    </svg>
  )
}

function Cyclist({ x, opacity, squash }: { x: number; opacity: number; squash: number }) {
  // Stretch horizontally as motion rises — the shape itself smears.
  return (
    <g transform={`translate(${x} 0) scale(${1 + squash} 1)`} opacity={opacity} fill="#d8d5cd">
      <circle cx="0" cy="68" r="3.4" />
      <path d="M-2.5 71 L 2 78 L 6 74" stroke="#d8d5cd" strokeWidth="2.6" fill="none" />
      <circle cx="-4" cy="84" r="5.6" fill="none" stroke="#d8d5cd" strokeWidth="1.7" />
      <circle cx="8" cy="84" r="5.6" fill="none" stroke="#d8d5cd" strokeWidth="1.7" />
    </g>
  )
}

/** A row of three frames with their setting labels and captions. */
export function FrameRow({ variants }: { variants: FrameVariant[] }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {variants.map((v) => (
        <figure key={v.label} className="min-w-0">
          <SampleFrame params={v.params} />
          <figcaption className="mt-1.5">
            <span
              className="block text-[12px] font-bold tabular-nums text-chrome"
              style={{ fontFamily: 'var(--font-dial)' }}
            >
              {v.label}
            </span>
            <span className="block text-[10.5px] leading-snug text-muted">{v.caption}</span>
          </figcaption>
        </figure>
      ))}
    </div>
  )
}
